import { useState, type ReactNode } from 'react';
import { ErrorCode } from '@brimo/shared';
import { ApiError, errorMessage } from '../../../lib/api';
import { normalizeTimeInput } from '../../../lib/time-input';
import { NextStepDialog } from '../../tasks/NextStepDialog';
import type { ContactRecord, TaskDetail, TaskNextStep } from '../types';

/** What every editor of the record (II.E1–E8) receives. */
export interface EditorProps {
  record: ContactRecord;
  /** The open task the editor was opened from (`?task=`), when it concerns this editor. */
  task: TaskDetail | null;
  onClose: () => void;
  /** The action succeeded: the page shows the toast, refetches the record and follows `next`. */
  onDone: (result: EditorResult) => void;
}

export interface EditorResult {
  toast: string;
  /** „Termin buchen" as the next step of a completed task: opens the appointment editor for that interim task. */
  next?: { editor: 'appointment'; completesTaskId: string | null } | undefined;
}

export function isNextStepRequired(error: unknown): boolean {
  return error instanceof ApiError && error.code === ErrorCode.NEXT_STEP_REQUIRED;
}

export function isValidation(error: unknown): error is ApiError {
  return error instanceof ApiError && error.code === ErrorCode.VALIDATION_FAILED;
}

/**
 * „Nächster Schritt fehlt" (Queue 07) for an action that would leave the contact without an open
 * step: the editor opens the gate on `409 NEXT_STEP_REQUIRED`, the dialog collects the step and
 * `retry` performs the action again with it.
 */
export function useNextStepGate(
  contactName: string,
  retry: (step: TaskNextStep) => Promise<void>,
): { open: () => void; dialog: ReactNode } {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(step: TaskNextStep) {
    setPending(true);
    setError(null);
    try {
      await retry(step);
      setOpen(false);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return {
    open: () => {
      setError(null);
      setOpen(true);
    },
    dialog: (
      <NextStepDialog
        open={open}
        contactName={contactName}
        pending={pending}
        error={error}
        onBack={() => {
          setOpen(false);
          setError(null);
        }}
        onSubmit={submit}
      />
    ),
  };
}

/** The follow-up of the editors (`followUp` of calls, messages, notes): only when the box is ticked. */
export function followUpOf(
  checked: boolean,
  date: string,
  time: string,
): { dueOn: string; dueTime?: string } | undefined {
  if (!checked || !date) return undefined;
  const dueTime = normalizeTimeInput(time);
  return dueTime ? { dueOn: date, dueTime } : { dueOn: date };
}

/** Bezug chips of an editor: the contact's deals and tickets from the linked groups, plus „nur Kontakt". */
export interface Reference {
  value: string;
  label: string;
  dealId?: string | undefined;
  ticketId?: string | undefined;
}

export function referencesOf(record: ContactRecord): Reference[] {
  const result: Reference[] = [];
  for (const group of record.linked) {
    if (group.key !== 'deals' && group.key !== 'tickets') continue;
    for (const item of group.items) {
      if (group.key === 'deals') {
        const head = item.title.split(' · ')[0] ?? item.title;
        result.push({
          value: `deal:${item.id}`,
          label: head.startsWith('Deal') ? head : `Deal ${head}`,
          dealId: item.id,
        });
      } else {
        const number = /#\d+/.exec(item.meta ?? '')?.[0];
        result.push({
          value: `ticket:${item.id}`,
          label: number ? `Ticket ${number}` : item.title,
          ticketId: item.id,
        });
      }
    }
  }
  result.push({ value: 'contact', label: 'nur Kontakt' });
  return result;
}
